import React, { useCallback, useContext, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';

import AccountPickerContext from '../contexts/AccountPickerContext';
import AnalyticsContext from '../contexts/AnalyticsContext';
import ConfigureDashboardGadgetContext from '../contexts/ConfigureDashboardGadgetContext';
import DashboardContext from '../contexts/DashboardContext';
import DashboardGadgetsContext from '../contexts/DashboardGadgetsContext';
import WallboardContext from '../contexts/WallboardContext';
import ErrorBoundary from '../components/Common/ErrorBoundary';
import ConfigureDashboardGadget from '../components/ConfigureDashboardGadget';
import updateDashboardGadget from '../api/updateDashboardGadget';
import isV5ChartDef from '../types/visTypeCheckers/isV5ChartDef';

const ConfigureDashboardGadgetProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { isWallboard } = useContext(WallboardContext);
  const { dashboardGadgetsLookup } = useContext(DashboardGadgetsContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dashboardGadget, setDashboardGadget] = useState<
    DashboardGadget | undefined
  >();
  const { dashboard } = useContext(DashboardContext);

  const open = useCallback(
    (gadget: DashboardGadget) => {
      // Always use the freshest version from cache
      const freshGadget = dashboardGadgetsLookup[gadget.id] || gadget;
      trackEvent('Dashboard Card - Opened Edit Screen', {
        isWallboardSlide: isWallboard ? 'true' : 'false',
      });
      setDashboardGadget(freshGadget);
      setIsOpen(true);
    },
    [dashboardGadgetsLookup, isWallboard, trackEvent],
  );

  const close = useCallback(() => {
    trackEvent('Dashboard Card - Edit - Cancelled', {
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });
    setIsOpen(false);
  }, [isWallboard, trackEvent]);

  const onSave = useCallback(
    (g: DashboardGadget) => {
      if (!dashboard) {
        const e = new Error();
        e.name =
          'ConfigureDashboardGadgetProvider#onSave - dashboard is not defined';
        throw e;
      }
      trackEvent('Dashboard Card - Edit - Saved', {
        type: isV5ChartDef(g.chartDef) ? g.chartDef.gadgetType : '',
        dashboardId: dashboard.id,
        dashboardName: dashboard.name,
        isWallboardSlide: isWallboard ? 'true' : 'false',
      });
      updateDashboardGadget(g.id, g, selectedAccountId).then(() =>
        setIsOpen(false),
      );
    },
    [dashboard, isWallboard, selectedAccountId, trackEvent],
  );

  if (!dashboard) {
    return <>{children}</>;
  }

  return (
    <ConfigureDashboardGadgetContext.Provider
      value={{
        isOpen,
        open,
        close,
      }}
    >
      {children}
      <ModalTransition>
        {isOpen && !!dashboardGadget && (
          <Modal
            width={'95vw'}
            height={'80vh'}
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            {/* A failure stays in the modal, which can still be closed */}
            <ErrorBoundary isRetryable>
              <WallboardContext.Provider
                value={{
                  isWallboard: false,
                  dashboardZoom: 1,
                }}
              >
                <ConfigureDashboardGadget
                  key={dashboardGadget.id}
                  editingGadget={dashboardGadget}
                  onEditSave={onSave}
                  close={close}
                />
              </WallboardContext.Provider>
            </ErrorBoundary>
          </Modal>
        )}
      </ModalTransition>
    </ConfigureDashboardGadgetContext.Provider>
  );
};

export default ConfigureDashboardGadgetProvider;
